import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import CancelIcon from 'icons/cancel.svg';
import CarbonCheckmarkFilled from 'icons/carbon-checkmark-filled.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import ResizableTruncatableText from 'components/application/ResizableTruncatableText';
import SidePanel from 'components/application/SidePanel';

import { formatRiskValue } from 'components/helpers/resources/riskMatrix';
import { resourceShape } from 'components/helpers/serialisableResources';

const SIZE_THRESHOLDS = { small: 4, medium: 7 };
const WIDTH_THRESHOLDS = { small: 4, medium: 6 };

const matrixStylingSizeMap = {
  small: { cellWidth: '120px', cellHeight: '48px', sidePanelWidth: '688px' },
  medium: { cellWidth: '104px', cellHeight: '42px', sidePanelWidth: '840px' },
  large: { cellWidth: '80px', cellHeight: '42px', sidePanelWidth: '1024px' },
};
export default function RiskSelectionSidePanel({
  riskMatrix,
  severityColourMap,
}) {
  const activeRiskSelectorRef = useRef(null);

  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [riskType, setRiskType] = useState(null);
  const [selectedEntryId, setSelectedEntryId] = useState(null);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'riskRegister.riskSelectionSidePanel.components.RiskSelectionSidePanel',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  if (!isSidePanelOpen) activeRiskSelectorRef.current = null;

  const sortedColumns = riskMatrix.included
    .filter((inclusion) => inclusion.type === 'riskMatrixColumn')
    .sort((a, b) => a.attributes.position - b.attributes.position);

  const sortedRows = riskMatrix.included
    .filter((inclusion) => inclusion.type === 'riskMatrixRow')
    .sort((a, b) => a.attributes.position - b.attributes.position);

  const entriesIndexedById = riskMatrix.included
    .filter((inclusion) => inclusion.type === 'riskMatrixEntry')
    .reduce((acc, entry) => {
      acc[entry.id] = entry;
      return acc;
    }, {});

  const sortedRowEntries = sortedRows
    .map((row, index) =>
      row.relationships.entries.data
        .map((entry) => ({
          entry: entriesIndexedById[entry.id],
          rowPosition: index + 1,
        }))
        .sort(
          (a, b) => a.entry.attributes.position - b.entry.attributes.position,
        ),
    )
    .flat();

  const gridColumnSize = sortedColumns.length;
  const gridRowSize = sortedRows.length;
  const isRowLegendVisible = sortedRows.some((row) => row.attributes.example);
  const isColumnLegendVisible = sortedColumns.some(
    (column) => column.attributes.example,
  );

  const handleRiskSelection = (entryId) => {
    setSelectedEntryId(entryId);
    // Imperatively setting value and style of displayed risk outside of the side panel
    const entry = entriesIndexedById[entryId];
    const { severity, value } = entry.attributes;

    const riskSelector = activeRiskSelectorRef.current;
    if (!riskSelector) return;

    const riskSelectorInput = riskSelector.querySelector(
      '[data-element-name="risk-selector-input"]',
    );
    const riskSelectorDisplay = riskSelector.querySelector(
      '[data-element-name="risk-display"]',
    );

    riskSelectorInput.value = entryId;
    riskSelectorDisplay.innerText = formatRiskValue(value);

    Object.keys(severityColourMap).forEach((severityName) => {
      const { backgroundColor, textColor } = severityColourMap[severityName];
      riskSelectorDisplay.classList.remove(backgroundColor, textColor);
    });

    riskSelectorDisplay.classList.add(
      severityColourMap[severity].backgroundColor,
      severityColourMap[severity].textColor,
    );
  };

  useEffect(() => {
    const handleRiskSelectorClick = (e) => {
      const riskSelector = e.target.closest(
        '[data-element-name="risk-selector"]',
      );

      if (!riskSelector) {
        setIsSidePanelOpen(false);
        return;
      }

      const dataRiskType = riskSelector.dataset.riskType;

      if (['initial', 'residual'].includes(dataRiskType)) {
        setRiskType(dataRiskType);
      }

      const riskSelectorInput = riskSelector.querySelector(
        '[data-element-name="risk-selector-input"]',
      );

      setSelectedEntryId(riskSelectorInput.value);

      activeRiskSelectorRef.current = riskSelector;

      setIsSidePanelOpen(true);
    };

    // Applying event listener to form to account for dynamically added tasks
    const riskAssessmentForm = document.querySelector(
      '[data-element-name="risk-assessment-form"]',
    );

    riskAssessmentForm.addEventListener('click', handleRiskSelectorClick);

    return () =>
      riskAssessmentForm.removeEventListener('click', handleRiskSelectorClick);
  }, []);

  function assignSize(size, thresholds) {
    if (size <= thresholds.small) return 'small';
    if (size <= thresholds.medium) return 'medium';
    return 'large';
  }

  const gridSize = (() => assignSize(gridColumnSize, SIZE_THRESHOLDS))();
  const gridWidth = (() => assignSize(gridColumnSize, WIDTH_THRESHOLDS))();
  const riskDisplayEntry = entriesIndexedById[selectedEntryId];

  const { columnsLabel, description, rowsLabel } = riskMatrix.data.attributes;

  return (
    <SidePanel
      bodyContent={
        <>
          <div className='tw-bg-white tw-pb-0 tw-pt-5'>
            <div
              className='tw-float-right tw-cursor-pointer tw-pr-5'
              data-element-name='sidepanel-close'
              onClick={() => setIsSidePanelOpen(false)}
            >
              <CancelIcon />
            </div>
            <div className='tw-clear-both tw-px-8'>
              <p className='tw-text-l tw-font-semibold tw-text-grey-600'>
                {i18nT('title')}
              </p>
              {riskType && (
                <p className='tw-text-xl tw-font-semibold tw-capitalize'>
                  {i18nT(`subtitle.${riskType}`)}
                </p>
              )}
            </div>
          </div>
          <div className='tw-p-6'>
            <div className='tw-border tw-mb-1 tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border-grey-200 tw-bg-grey-025 tw-p-6'>
              <div className='tw-h-3.5 tw--rotate-90'>
                <span className='tw-text-m tw-font-bold'>{rowsLabel}</span>
              </div>
              <div className='tw-flex tw-flex-col tw-items-center'>
                <div className='tw-my-4 tw-h-3.5 tw-text-m tw-font-bold'>
                  {columnsLabel}
                </div>
                <div
                  className='tw-relative tw-grid tw-gap-0.5 tw-border-0 tw-border-l-1 tw-border-t-1 tw-border-solid tw-border-grey-400'
                  style={{
                    gridTemplateColumns: `88px repeat(${gridColumnSize}, ${matrixStylingSizeMap[gridSize].cellWidth})`,
                    gridTemplateRows: `52px repeat(${gridRowSize}, ${matrixStylingSizeMap[gridSize].cellHeight})`,
                  }}
                >
                  {sortedColumns.map((column) => (
                    <div
                      className={classNames(
                        `tw-col-span-1 tw-my-2 tw-flex tw-items-end tw-justify-center tw-text-wrap tw-px-1 tw-text-center tw-text-s tw-font-bold tw-text-grey-600`,
                        `tw-col-start-${column.attributes.position + 1}`,
                      )}
                      key={column.id}
                    >
                      <ResizableTruncatableText
                        className='tw-line-clamp-2 tw-whitespace-normal'
                        text={column.attributes.label}
                      />
                    </div>
                  ))}
                  {sortedRows.map((row) => (
                    <div
                      className={classNames(
                        'tw-col-span-1 tw-col-start-1 tw-mx-2 tw-flex tw-items-center tw-justify-end tw-text-right tw-text-s tw-font-bold tw-text-grey-600',
                        `tw-row-start-${row.attributes.position + 1}`,
                      )}
                      key={row.id}
                    >
                      <ResizableTruncatableText
                        className='tw-line-clamp-2 tw-whitespace-normal'
                        text={row.attributes.label}
                      />
                    </div>
                  ))}
                  {sortedRowEntries.map(({ entry, rowPosition }) => (
                    <div
                      className={classNames(
                        'hover:tw-bg-alpha-100 tw-relative tw-col-span-1 tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-px-3 tw-py-2 tw-text-m tw-font-semibold',
                        `tw-col-start-${entry.attributes.position + 1}`,
                        `tw-row-start-${rowPosition + 1}`,
                        severityColourMap[entry.attributes.severity]
                          .backgroundColor,
                        severityColourMap[entry.attributes.severity].textColor,
                        selectedEntryId === entry.id ?
                          'tw-bg-alpha-100 tw-border-2 tw-border-solid tw-border-black/60'
                        : 'tw-bg-alpha-75',
                      )}
                      data-value={entry.attributes.value}
                      key={entry.id}
                      onClick={() => handleRiskSelection(entry.id)}
                    >
                      {selectedEntryId === entry.id && (
                        <CarbonCheckmarkFilled className='tw-absolute tw-right-0.5 tw-top-0.5 tw-h-3 tw-w-3' />
                      )}
                      <ResizableTruncatableText text={entry.attributes.value} />
                    </div>
                  ))}
                  <div className='tw-absolute tw-right-0 tw-top-[-4.5px] tw-box-border tw-h-2 tw-w-2 tw-rotate-45 tw-border-0 tw-border-r-1 tw-border-t-1 tw-border-solid tw-border-grey-400' />
                  <div className='tw-absolute tw-bottom-0 tw-left-[-4.5px] tw-box-border tw-h-2 tw-w-2 tw-rotate-45 tw-border-0 tw-border-b-1 tw-border-r-1 tw-border-solid tw-border-grey-400' />
                </div>
                {riskDisplayEntry && (
                  <div className='tw-mt-3 tw-flex tw-items-center tw-gap-2 tw-rounded-[36px] tw-bg-grey-100 tw-pb-1 tw-pl-1 tw-pr-4 tw-pt-1 tw-text-m tw-font-semibold'>
                    <div
                      className={classNames(
                        'tw-flex tw-h-6 tw-min-w-6 tw-items-center tw-justify-center tw-rounded-full tw-border-2 tw-border-solid tw-border-black tw-p-1.5',
                        severityColourMap[riskDisplayEntry.attributes.severity]
                          .backgroundColor,
                        severityColourMap[riskDisplayEntry.attributes.severity]
                          .textColor,
                      )}
                    >
                      {formatRiskValue(riskDisplayEntry.attributes.value)}
                    </div>
                    <div className='tw-capitalize'>
                      {i18nT(`subtitle.${riskType}`)}
                    </div>
                  </div>
                )}
              </div>
            </div>
            {description && (
              <div className='tw-border tw-mt-4 tw-rounded-lg tw-border-grey-200 tw-bg-grey-025 tw-px-5 tw-py-4'>
                <div className='tw-border-0 tw-border-b-1 tw-border-solid tw-border-grey-300 tw-pb-3 tw-text-l tw-font-semibold'>
                  {i18nTShared('description')}
                </div>
                <div className='tw-pt-4'>{description}</div>
              </div>
            )}
            {isRowLegendVisible && (
              <div className='tw-border tw-mt-4 tw-rounded-lg tw-border-grey-200 tw-bg-grey-025 tw-px-5 tw-py-4'>
                <div className='tw-border-0 tw-border-b-1 tw-border-solid tw-border-grey-300 tw-pb-3 tw-text-l tw-font-semibold'>
                  {rowsLabel}
                </div>
                <div className='tw-grid tw-grid-cols-[auto_1fr] tw-gap-x-1 tw-pt-4 tw-text-m'>
                  {sortedRows.map((row) => (
                    <React.Fragment key={row.id}>
                      <div className='tw-font-semibold'>
                        {row.attributes.index}.
                      </div>
                      <div>
                        <span className='tw-font-semibold'>
                          {row.attributes.label}
                        </span>
                        {row.attributes.example && (
                          <span className='tw-font-normal'>
                            : {row.attributes.example}
                          </span>
                        )}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
            {isColumnLegendVisible && (
              <div className='tw-border tw-mt-4 tw-rounded-lg tw-border-grey-200 tw-bg-grey-025 tw-px-5 tw-py-4'>
                <div className='tw-border-0 tw-border-b-[1px] tw-border-solid tw-border-grey-300 tw-pb-3 tw-text-l tw-font-semibold'>
                  {columnsLabel}
                </div>
                <div className='tw-grid tw-grid-cols-[auto_1fr] tw-gap-x-1 tw-pt-4 tw-text-m'>
                  {sortedColumns.map((column) => (
                    <React.Fragment key={column.id}>
                      <div className='tw-font-semibold'>
                        {column.attributes.index}.
                      </div>
                      <div>
                        <span className='tw-font-semibold'>
                          {column.attributes.label}
                        </span>
                        {column.attributes.example && (
                          <span className='tw-font-normal'>
                            : {column.attributes.example}
                          </span>
                        )}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      }
      closeCallback={() => setIsSidePanelOpen(false)}
      color='tertiary'
      isOpen={isSidePanelOpen}
      scrollHeader={false}
      sidePanelClassName='tw-z-[12000] tw-overflow-x-hidden tw-box-border'
      sidePanelStyle={{
        width: `${matrixStylingSizeMap[gridWidth].sidePanelWidth}`,
      }}
    />
  );
}

RiskSelectionSidePanel.propTypes = {
  riskMatrix: PropTypes.shape({ data: resourceShape('riskMatrix').isRequired }),
  severityColourMap: PropTypes.object.isRequired,
};
